import { Controller } from '@hotwired/stimulus'
import { getDisplayValues } from '../helpers/computedValues'
import {
  connectUserData,
  getComponentContext,
  getFlow,
  lifecycleTrigger,
} from '../helpers/controllerHelpers/utils'
import { isBuilderEditingMode } from '../helpers/utils'
import type { Base, UserData, UserDataValue } from '../api/models/types'
import _ from 'lodash'
import replaceStorageURLsFromFirebaseToCloudflare from '../api/helpers/replaceStorageURLsFromFirebaseToCloudflare'
import renderTemplate from '../api/helpers/templating'

export default class extends Controller {
  static values = {
    text: String,
    target: String,
    watchContents: Boolean,
    avoidRerendering: Boolean,
    alwaysRerender: Boolean,
    isEditingFlow: Boolean,
    // userData: Object,
  }

  declare textValue: string | null
  declare targetValue: string | null
  disconnectUserData: (() => void) | undefined
  declare userDataValue: UserDataValue & { current_page_key: string }
  declare watchContentsValue: boolean
  declare alwaysRerenderValue: boolean
  declare observer: MutationObserver
  declare lastVal: string
  declare isEditingFlowValue: boolean
  declare removeListeners: () => void
  component: Base
  flowEl: Element | null
  stopWatchingForEditing: () => void
  declare forceUpdate: boolean

  connect() {
    const self = this

    try {
      if (this.textValue) this.textValue = decodeURIComponent(this.textValue)
    } catch (e) {
      //* We decided together to do an empty catch on a dev call
    }

    if (!this.textValue) this.textValue = this._getText()
    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)
    this.userDataValueChanged()

    if (this.watchContentsValue) {
      // Callback function to execute when mutations are observed
      const callback = () => {
        self.userDataValueChanged()
      }

      // Create an observer instance linked to the callback function
      this.observer = new MutationObserver(callback)

      // Start observing the target node for configured mutations
      this.observer.observe(this.element, { childList: true })
    }
    const { component, flowEl } = getComponentContext(this)
    this.component = component as Base
    this.flowEl = flowEl

    this.stopWatchingForEditing = this.watchForEditing()

    lifecycleTrigger(this, 'mounted')

    //* Listen for language updates, we need to force update the template if the language changes
    this.element.addEventListener('language:updated', () => {
      this.textValue = this._getText()
      this.forceUpdate = true
      this.userDataValueChanged()
    })
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    this.stopWatchingForEditing()
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
    if (this.observer) this.observer.disconnect()
  }

  userDataValueChanged() {
    if (!this.element || !this.element.parentElement) {
      return
    }
    if (!this.textValue) return
    if (this.textValue === 'undefined') return
    // @TODO: See if this works well, and if it helps with performance:
    if (!this.textValue.includes('{{')) return

    // @TODO: Recursively include all levels of nested localData
    // @TODO: Do this in the renderer too
    // @TODO: DO this in other places that use userData
    //          - Image URL + other props
    //          - Sub-repeatables
    //          - Anythign else?
    const localData = this.element
      .closest('[data-component-local-data-value]')
      ?.getAttribute('data-component-local-data-value')
    const userData = {
      ...this.userDataValue,
      _display_values: getDisplayValues(this.userDataValue, getFlow(this)),
      ...(localData ? { item: JSON.parse(localData) } : {}),
    }

    const text = this.isEditingFlowValue ? this.textValue : renderTemplate(this.textValue, userData)

    const textWithImageReplace = replaceStorageURLsFromFirebaseToCloudflare(text)

    const shouldUpdate = this.alwaysRerenderValue
      ? this._getText() !== textWithImageReplace
      : this.lastVal !== textWithImageReplace

    if (this.forceUpdate) {
      this._setText(textWithImageReplace)
      this.forceUpdate = false
      return
    }
    if (shouldUpdate) this._setText(textWithImageReplace)
  }

  _getText() {
    return this.targetValue ? this.element.getAttribute(this.targetValue) : this.element.innerHTML
  }

  _setText(val) {
    if (this.targetValue) {
      if (val) this.element.setAttribute(this.targetValue, val)
      else this.element.removeAttribute(this.targetValue)
    } else this.element.innerHTML = val
    this.lastVal = val
  }

  watchForEditing() {
    if (!isBuilderEditingMode()) {
      return () => {}
    }

    const targetElement = this.element

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-edit-editing-value'
        ) {
          this.isEditingFlowValue = targetElement.getAttribute(mutation.attributeName) === 'true'
          this.userDataValueChanged()
        }
      })
    })

    observer.observe(targetElement, { attributes: true })

    return () => {
      observer.disconnect()
    }
  }
}
